import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import {
  b2fileid,
  evaluationid,
  invoiceid,
  lesson_removed_reason,
  lesson_status,
  lessongroupid,
  lessonid,
  teacherid,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import * as routes from "../../routes";
import { filenote, textnote } from "../notes/noteswidget";
import { LocalDate, LocalTime } from "@js-joda/core";
import { checkAllCasesHandled, mapPartial } from "trader.ts/utils";
import * as FA from "../../../styles/fontawesome.css.classes";
import { isEqual } from "../../lib/isequal";
import { makeDateRange } from "../../lib/time";
import {
  parseLessonRemovedReason,
  parseLessonStatus,
  parseWeekday,
} from "../../parsing_from_ur";
import { ddMMMMyyyy } from "../../render";
import { renderTimeLinePart } from "../notes/client";
import * as noteswidget from "../notes/noteswidget";
import {
  enrollmentStudentStart,
  renderEnrollmentCard,
} from "../student_start/client";
import currency from "currency.js";

export type enrollmentDetailsStudentStart = {
  enrollment: enrollmentStudentStart;
  evaluations: {
    id: evaluationid;
    finalized: LocalDate;
  }[];
  invoice: {
    id: invoiceid;
    externalid: number;
    amount_total: currency;
    amount_paid: currency;
    amount_credited: currency;
    amount_left: currency;
  } | null;
  textnotes: textnote[];
  filenotes: filenote[];
  lessons: {
    id: lessonid;
    lessongroupid: lessongroupid;
    plannedteacherid: teacherid;
    teacherid: teacherid;
    date: LocalDate;
    removedreason: lesson_removed_reason | null;
    time: LocalTime;
    replacement: lessonid | null;
    sequence_number: number | null;
  }[];
  holidays: {
    description: string;
    firstday: LocalDate;
    lastday: LocalDate;
  }[];
  student_absences: {
    lessonid: lessonid;
    status: lesson_status;
    createdby: string;
    createdon: LocalDate;
    comments: string;
    storagefileid: b2fileid | null;
  }[];
};

export function render(opts: {
  enrollment: enrollmentDetailsStudentStart;
  schoolname: string;
}): HTMLElement | HTMLElement[] {
  const details = opts.enrollment;
  const today = LocalDate.now();

  const ordered = details.lessons.orderBy((l) => l.date.toEpochDay(), "asc");
  const lessonToEmphasize = ordered.find((l) => l.date.isAfter(today));

  const reload = () => location.reload();

  const renderedLessons = mapPartial(
    ordered,
    (l): [LocalDate, HTMLElement] | null => {
      const removedReason =
        l.removedreason && parseLessonRemovedReason(l.removedreason);
      if (
        removedReason === "DeletedManually" ||
        removedReason === "RemovedBecauseOfNewHoliday" ||
        removedReason === "RemovedBecauseOfRemovalOfHoliday"
      ) {
        return null;
      }
      const removedLabel =
        removedReason === null
          ? null
          : removedReason === "MovedDueToTeacherAbsence"
          ? "Docent afwezig, vervangles ingepland"
          : removedReason === "DeletedDueToTeacherAbsence"
          ? "Docent afwezig"
          : removedReason === "StudentLegalAbsence"
          ? "Student afwezig, vervangles ingepland"
          : checkAllCasesHandled(removedReason);

      const absence = details.student_absences.find(
        (abs) =>
          abs.lessonid === l.id &&
          parseLessonStatus(abs.status) === "StudentAbsent",
      );

      const writingS = new Source(null);
      const notesRendered = noteswidget.renderPerLesson(
        opts.schoolname,
        l.id,
        {
          textnotes: details.textnotes,
          filenotes: details.filenotes,
        },
        { id: l.lessongroupid, students: [] },
        {
          writingS,
          smallbuttons: true,
          reload,
          allowInput: absence ? false : true,
          fullWidthButtons: false,
          userIsStudent: true,
        },
      );

      const absenceRendered = absence ? (
        <div className={style.panel} style="background-color: var(--grey-100)">
          <div style="margin-bottom: 8px;">
            <div style="font-weight: bold;">
              {"Afwezigheid doorgegeven"}
            </div>
          </div>
          <div style="margin-bottom: 8px;" className={style.text_with_icon}>
            <i className={FA.icon_envelope_solid}></i>
            <div>
              {absence.comments ? (
                <span>{absence.comments}</span>
              ) : (
                <span style="text-style: italic;">
                  {"Geen reden doorgegeven"}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <span></span>
      );

      const timelinePart = renderTimeLinePart({
        outline: removedReason !== null,
        emphasize: l.id === lessonToEmphasize?.id,
        extraStyle: "",
        num: l.sequence_number,
        contents: [
          <div>{ddMMMMyyyy(l.date)}</div>,
          <div>
            {removedLabel ? (
              <span style="font-style: italic">{removedLabel}</span>
            ) : (
              [notesRendered, absenceRendered]
            )}
          </div>,
        ],
      });
      return [l.date, timelinePart];
    },
  );

  const renderedHolidays = details.holidays.flatMap((holiday) => {
    return makeDateRange(holiday.firstday, holiday.lastday)
      .filter((d) =>
        !details.enrollment.lessongroup.firstlesson ||
        !details.enrollment.lessongroup.lastlesson
          ? false
          : d.isAfter(details.enrollment.lessongroup.firstlesson) &&
            d.isBefore(details.enrollment.lessongroup.lastlesson) &&
            isEqual(
              d.dayOfWeek(),
              parseWeekday(details.enrollment.lessongroup.weekday),
            ),
      )
      .map((holidayDate): [LocalDate, HTMLElement] => {
        return [
          holidayDate,
          renderTimeLinePart({
            outline: true,
            emphasize: false,
            extraStyle: "",
            num: null,
            contents: [
              <div>{ddMMMMyyyy(holidayDate)}</div>,
              <div style="font-style: italic;">{holiday.description}</div>,
            ],
          }),
        ];
      });
  });

  const lessons = [...renderedLessons, ...renderedHolidays]
    .orderBy(([d, _h]) => d.toEpochDay())
    .map(([_d, h]) => h);
  const detailsRendered = (
    <div>
      {lessonToEmphasize &&
      lessonToEmphasize.sequence_number &&
      lessonToEmphasize.sequence_number > 5 ? (
        <div className={style.text_with_icon} style="margin-bottom: 32px;">
          <i
            className={FA.icon_arrow_right}
            style="transform: rotate(90deg);"
          ></i>
          <a
            onclick={() =>
              document
                .querySelector("." + style.timeline_emphasis)
                ?.scrollIntoView()
            }
          >
            {"Spring naar eerstvolgende les"}
          </a>
        </div>
      ) : (
        <span></span>
      )}
      {lessons}
    </div>
  );

  const backLink = (
    <div className={style.text_with_icon} style="margin-bottom: 12px;">
      <i className={FA.icon_arrow_left}></i>
      <a href={routes.student_startRoute.link()}>
        <div>{"Terug"}</div>
      </a>
    </div>
  );

  const header = renderEnrollmentCard(
    { showStudentName: true },
    details.enrollment,
  );

  return [
    <div
      className={style.full_width_center_800}
      style="background-color: var(--grey-100);"
    >
      {backLink}
      {header}
    </div>,
    <main
      className={style.full_width_center_800}
      style="max-width: unset; background-color: white "
    >
      {detailsRendered}
    </main>,
  ];
}
